import { useState, type KeyboardEvent } from "react";
import { useClerk, useUser } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import {
  Menu,
  Moon,
  Sun,
  Monitor,
  LogOut,
  Bell,
  Check,
  ChevronsUpDown,
  Trash2,
  Users,
} from "lucide-react";
import {
  AvatarWithSkeleton,
  Button,
  CircleImageWithSkeleton,
  cn,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@cliently/ui";
import { useUiStore } from "../../stores/ui.store";
import { useOrgStore } from "../../stores/org.store";
import { ALL_CLIENTS_KEY } from "../../stores/org.store";
import { useOrganizations } from "../../hooks/use-organizations";
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
  useDeleteReadNotifications,
} from "../../hooks/use-notifications";
import { useClientProfile } from "../../hooks/use-client-profile";
import { resolveOrganizationMediaUrl } from "../../lib/organization-media-url";
import { api } from "../../lib/api";
import { getClientNotificationPath } from "../../lib/notification-navigation";

const languages = [
  { id: "fr", code: "fr", label: "Français" },
  { id: "en", code: "gb", label: "English" },
  { id: "es", code: "es", label: "Español" },
  { id: "zh", code: "cn", label: "中文" },
] as const;



const FlagImg = ({ code, className }: { code: string; className?: string }) => {
  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
      alt={code}
      className={`inline-block h-4 w-6 rounded-sm object-cover ${className ?? ""}`}
    />
  );
}



const timeAgo = (dateStr: string, t: (k: string, d: string) => string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t("notifications.justNow", "À l'instant");
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}j`;
}



export const ClientHeader = () => {
  const { toggleSidebar, theme, setTheme } = useUiStore();
  const { signOut } = useClerk();
  const { user } = useUser();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState<boolean>(false);

  const { data: notifications } = useNotifications();
  const { data: unread } = useUnreadCount();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const deleteNotification = useDeleteNotification();
  const deleteReadNotifications = useDeleteReadNotifications();
  const [notifTab, setNotifTab] = useState<"all" | "unread">("all");

  const { data: organizations } = useOrganizations();
  const { activeClientId, switchOrg, getActiveOrg } = useOrgStore();
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;
  const { data: portalProfile } = useClientProfile();
  const activeOrg = getActiveOrg();

  const handleSwitchOrg = (clientId: string | null) => {
    if (clientId === activeClientId) return;
    switchOrg(clientId);
    queryClient.invalidateQueries();
    navigate("/dashboard");
  };

  const currentLang =
    languages.find((l) => i18n.language.startsWith(l.id)) || languages[0];

  const cycleTheme = () => {
    const next =
      theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
    setTheme(next);
  };

  const ThemeIcon = theme === "light" ? Sun : theme === "dark" ? Moon : Monitor;

  const goToSettings = () => navigate("/settings");

  const showOrgSwitcher = organizations && organizations.length >= 2;
  const activeOrgImage = resolveOrganizationMediaUrl(
    activeOrg?.orgProfilePhoto ?? activeOrg?.orgLogo,
  );
  const orgSwitcherTriggerLabel = isAllProsMode
    ? t("header.allPros", "Tous mes pros")
    : (activeOrg?.orgName ??
      t("header.selectOrg", "Choisir un praticien"));

  const confirmLogout = () => {
    setLogoutDialogOpen(false);
    void signOut();
  };

  return (
    <>
      <header className="bg-background/95 sticky top-0 z-30 flex h-14 items-center justify-between border-b px-4 backdrop-blur">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="md:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>

          {showOrgSwitcher && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 max-md:gap-1 max-md:px-2"
                  aria-label={orgSwitcherTriggerLabel}
                >
                  {isAllProsMode ? (
                    <Users
                      className="text-muted-foreground h-5 w-5 shrink-0"
                      aria-hidden
                    />
                  ) : activeOrgImage ? (
                    <CircleImageWithSkeleton
                      src={activeOrgImage}
                      alt=""
                      className="h-5 w-5 shrink-0"
                    />
                  ) : activeOrg?.orgName ? (
                    <div className="bg-primary/10 text-primary flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold">
                      {activeOrg.orgName[0]}
                    </div>
                  ) : null}
                  <span className="hidden max-w-[160px] truncate text-sm font-medium md:inline">
                    {orgSwitcherTriggerLabel}
                  </span>
                  <ChevronsUpDown className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[200px]">
                <DropdownMenuItem
                  onClick={() => handleSwitchOrg(null)}
                  className={cn(
                    "flex items-center gap-2",
                    activeClientId === ALL_CLIENTS_KEY && "bg-accent",
                  )}
                >
                  <Users
                    className="text-muted-foreground h-5 w-5 shrink-0"
                    aria-hidden
                  />
                  <span className="truncate">
                    {t("header.allPros", "Tous mes pros")}
                  </span>
                  {activeClientId === ALL_CLIENTS_KEY && (
                    <Check className="text-primary ml-auto h-4 w-4" />
                  )}
                </DropdownMenuItem>
                {organizations.map((org) => {
                  const orgImage = resolveOrganizationMediaUrl(
                    org.orgProfilePhoto ?? org.orgLogo,
                  );
                  return (
                    <DropdownMenuItem
                      key={org.clientId}
                      onClick={() => handleSwitchOrg(org.clientId)}
                      className={cn(
                        "flex items-center gap-2",
                        org.clientId === activeClientId && "bg-accent",
                      )}
                    >
                      {orgImage ? (
                        <CircleImageWithSkeleton
                          src={orgImage}
                          alt=""
                          className="h-5 w-5"
                          fallback={
                            <div className="bg-primary/10 text-primary flex h-full w-full items-center justify-center rounded-full text-[10px] font-bold">
                              {org.orgName[0]}
                            </div>
                          }
                        />
                      ) : (
                        <div className="bg-primary/10 text-primary flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold">
                          {org.orgName[0]}
                        </div>
                      )}
                      <span className="truncate">{org.orgName}</span>
                      {org.clientId === activeClientId && (
                        <Check className="text-primary ml-auto h-4 w-4" />
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-4 w-4" />
                {(unread?.count ?? 0) > 0 && (
                  <span className="bg-destructive text-destructive-foreground absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold">
                    {unread!.count > 9 ? "9+" : unread!.count}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80" onCloseAutoFocus={(e) => e.preventDefault()}>
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-sm font-semibold">
                  {t("notifications.title", "Notifications")}
                </span>
                <div className="flex items-center gap-1">
                  {(unread?.count ?? 0) > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto px-2 py-1 text-xs"
                      onClick={(e) => { e.stopPropagation(); markAllAsRead.mutate(); }}
                    >
                      <Check className="mr-1 h-3 w-3" />
                      {t("notifications.markAllRead", "Tout marquer lu")}
                    </Button>
                  )}
                  {notifications && notifications.some((n) => n.read) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive h-8 w-8"
                      onClick={(e) => { e.stopPropagation(); deleteReadNotifications.mutate(); }}
                      aria-label={t("notifications.deleteRead", "Supprimer les lues")}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex border-b px-3">
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(
                    "h-auto rounded-none px-0 pb-1.5 text-xs font-medium shadow-none hover:bg-transparent",
                    notifTab === "all"
                      ? "border-primary text-primary border-b-2"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  onClick={(e) => { e.stopPropagation(); setNotifTab("all"); }}
                >
                  {t("notifications.tabAll", "Toutes")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(
                    "ml-4 h-auto rounded-none px-0 pb-1.5 text-xs font-medium shadow-none hover:bg-transparent",
                    notifTab === "unread"
                      ? "border-primary text-primary border-b-2"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  onClick={(e) => { e.stopPropagation(); setNotifTab("unread"); }}
                >
                  {t("notifications.tabUnread", "Non lues")}
                  {(unread?.count ?? 0) > 0 && (
                    <span className="bg-destructive text-destructive-foreground ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold">
                      {unread!.count > 9 ? "9+" : unread!.count}
                    </span>
                  )}
                </Button>
              </div>
              <DropdownMenuGroup className="max-h-80 overflow-y-auto">
                {(() => {
                  const filtered = notifications
                    ? notifTab === "unread"
                      ? notifications.filter((n) => !n.read)
                      : notifications
                    : [];
                  if (filtered.length === 0) {
                    return (
                      <div className="text-muted-foreground px-3 py-6 text-center text-sm">
                        {notifTab === "unread"
                          ? t("notifications.emptyUnread", "Aucune notification non lue")
                          : t("notifications.empty", "Aucune notification")}
                      </div>
                    );
                  }
                  return filtered.slice(0, 20).map((notif) => (
                    <DropdownMenuItem
                      key={notif.id}
                      className={cn(
                        "group flex items-start gap-2 px-3 py-2 data-[highlighted]:bg-primary data-[highlighted]:text-primary-foreground",
                        !notif.read && "bg-primary/5",
                      )}
                      onClick={() => {
                        if (!notif.read) markAsRead.mutate(notif.id);
                        const path = getClientNotificationPath(notif);
                        if (path) navigate(path);
                      }}
                    >
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span
                          className={cn(
                            "text-sm group-data-[highlighted]:text-primary-foreground",
                            !notif.read && "font-semibold",
                          )}
                        >
                          {notif.title}
                        </span>
                        <span className="text-muted-foreground group-data-[highlighted]:text-primary-foreground/90 line-clamp-1 text-xs">
                          {notif.message}
                        </span>
                        <span className="text-muted-foreground/60 group-data-[highlighted]:text-primary-foreground/80 text-[10px]">
                          {timeAgo(notif.createdAt, t)}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive mt-0.5 h-7 w-7 shrink-0 opacity-0 shadow-none transition-opacity group-hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification.mutate(notif.id);
                        }}
                        aria-label={t("notifications.delete", "Supprimer")}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuItem>
                  ));
                })()}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="ghost" size="icon" onClick={cycleTheme}>
            <ThemeIcon className="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <FlagImg code={currentLang.code} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-40">
              {languages.map((lang) => (
                <DropdownMenuItem
                  key={lang.id}
                  onClick={() => {
                    void i18n.changeLanguage(lang.id);
                    if (!isAllProsMode) {
                      void api.patch("/portal/me", { preferredLocale: lang.id });
                    }
                  }}
                  className={
                    i18n.language.startsWith(lang.id) ? "bg-accent" : ""
                  }
                >
                  <FlagImg code={lang.code} />
                  <span>{lang.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-center gap-2">
            <AvatarWithSkeleton
              className="h-8 w-8 cursor-pointer"
              role="button"
              tabIndex={0}
              aria-label={t("nav.settings", "Paramètres")}
              onClick={goToSettings}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  goToSettings();
                }
              }}
              src={
                resolveOrganizationMediaUrl(portalProfile?.photo) ??
                user?.imageUrl ??
                undefined
              }
              alt={user?.fullName ?? ""}
              fallback={
                <span className="text-xs">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </span>
              }
            />
            <div className="hidden md:block">
              <p className="text-sm font-medium">
                {user?.firstName} {user?.lastName}
              </p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLogoutDialogOpen(true)}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <Dialog
        open={logoutDialogOpen}
        onOpenChange={(open) => setLogoutDialogOpen(open)}
      >
        <DialogContent className="sm:max-w-md sm:rounded-xl">
          <DialogHeader className="space-y-2">
            <DialogTitle>
              {t("settings.logout.confirmTitle", "Confirmer la déconnexion ?")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "settings.logout.confirmDescription",
                "Vous allez être déconnecté de votre compte. Voulez-vous continuer ?",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setLogoutDialogOpen(false)}
            >
              {t("common.cancel", "Annuler")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmLogout}
            >
              {t("nav.logout", "Se déconnecter")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
